import { useState } from 'react';
import { ChevronDown, ChevronUp, Copy, Check, ExternalLink } from 'lucide-react';

interface CopyButtonProps {
  text: string;
  field: string;
}

export function FacebookSetupGuide() {
  const [isExpanded, setIsExpanded] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const copyToClipboard = async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  const CopyBtn = ({ text, field }: CopyButtonProps) => (
    <button
      onClick={() => copyToClipboard(text, field)}
      className="inline-flex items-center space-x-1 px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded border border-gray-300 transition-colors flex-shrink-0"
    >
      {copiedField === field ? (
        <><Check className="w-3 h-3 text-green-600" /><span className="text-green-600">Skopiowano</span></>
      ) : (
        <><Copy className="w-3 h-3" /><span>Kopiuj</span></>
      )}
    </button>
  );

  return (
    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-lg overflow-hidden">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-4 hover:bg-blue-100/50 transition-colors"
      >
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
            <span className="text-white text-sm font-bold">📘</span>
          </div>
          <div className="text-left">
            <h4 className="text-sm font-semibold text-blue-900">
              Instrukcja konfiguracji Facebook App
            </h4>
            <p className="text-xs text-blue-700">
              Przewodnik krok po kroku dla integracji z AI Agent HUB
            </p>
          </div>
        </div>
        {isExpanded ? (
          <ChevronUp className="w-5 h-5 text-blue-600" />
        ) : (
          <ChevronDown className="w-5 h-5 text-blue-600" />
        )}
      </button>

      {isExpanded && (
        <div className="px-4 pb-4 space-y-4">
          {/* Step 1 */}
          <div className="bg-white rounded-lg p-4 border-l-4 border-blue-600">
            <h5 className="text-sm font-semibold text-gray-800 mb-2">
              <span className="inline-block bg-blue-600 text-white text-xs px-2 py-0.5 rounded-full mr-2">1</span>
              Inicjalizacja aplikacji
            </h5>
            <p className="text-sm text-gray-700">
              Zaloguj się na{' '}
              <a href="https://developers.facebook.com/apps/" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800 inline-flex items-center gap-1">
                developers.facebook.com/apps/ <ExternalLink className="w-3 h-3" />
              </a>{' '}
              i kliknij przycisk <strong>Create App</strong>.
            </p>
            <p className="text-sm text-gray-700 mt-2">
              Wypełnij <strong>App Details</strong>: podaj nazwę aplikacji oraz Twój kontaktowy adres e-mail.
            </p>
          </div>

          {/* Step 2 */}
          <div className="bg-white rounded-lg p-4 border-l-4 border-blue-600">
            <h5 className="text-sm font-semibold text-gray-800 mb-2">
              <span className="inline-block bg-blue-600 text-white text-xs px-2 py-0.5 rounded-full mr-2">2</span>
              Wybór Use Cases i Biznesu
            </h5>
            <p className="text-sm text-gray-700">
              W sekcji <strong>Use cases</strong> zaznacz odpowiednie scenariusze użycia dla swojej aplikacji.
            </p>
            <p className="text-sm text-gray-700 mt-2">
              Wybierz <strong>Business Portfolio</strong>, do którego aplikacja ma zostać podłączona. Kliknij <strong>Next</strong>, aż przejdziesz do podsumowania, a następnie wybierz <strong>Go to dashboard</strong>.
            </p>
          </div>

          {/* Step 3 */}
          <div className="bg-white rounded-lg p-4 border-l-4 border-blue-600">
            <h5 className="text-sm font-semibold text-gray-800 mb-2">
              <span className="inline-block bg-blue-600 text-white text-xs px-2 py-0.5 rounded-full mr-2">3</span>
              Ustawienia Podstawowe (Settings)
            </h5>
            <p className="text-sm text-gray-700">
              Przejdź do <strong>App settings → Podstawowe</strong> i uzupełnij poniższe pola adresem:
            </p>
            <div className="mt-2 bg-gray-50 border border-gray-200 rounded p-2 flex items-center gap-2">
              <code className="text-xs text-gray-900 break-all flex-1 min-w-0">
                https://panel.aiagenthub.pl/
              </code>
              <CopyBtn text="https://panel.aiagenthub.pl/" field="guide-panel-url" />
            </div>
            <ul className="text-sm text-gray-700 list-disc list-inside mt-2 space-y-1 ml-2">
              <li>App domains</li>
              <li>Privacy policy URL</li>
              <li>User data deletion</li>
              <li>Terms of Service URL</li>
            </ul>
            <p className="text-sm text-gray-700 mt-3">
              Kliknij <strong>+Add Platform</strong>, wybierz <strong>Website</strong> i w polu <em>Site URL</em> wpisz:
            </p>
            <div className="mt-2 bg-gray-50 border border-gray-200 rounded p-2 flex items-center gap-2">
              <code className="text-xs text-gray-900 break-all flex-1 min-w-0">
                https://sms.aiagenthub.pl/
              </code>
              <CopyBtn text="https://sms.aiagenthub.pl/" field="guide-site-url" />
            </div>
            <p className="text-xs text-gray-500 mt-1">Zapisz zmiany.</p>
          </div>

          {/* Step 4 */}
          <div className="bg-white rounded-lg p-4 border-l-4 border-blue-600">
            <h5 className="text-sm font-semibold text-gray-800 mb-2">
              <span className="inline-block bg-blue-600 text-white text-xs px-2 py-0.5 rounded-full mr-2">4</span>
              Uprawnienia i Webhooki
            </h5>
            <p className="text-sm text-gray-700">
              W sekcji <strong>Use cases</strong> kliknij <strong>Customize</strong> (np. przy <em>Marketing API</em>).
            </p>
            <p className="text-sm text-gray-700 mt-2">
              W zakładce <strong>Permissions and features</strong> dodaj niezbędne uprawnienia tak, aby status <code className="bg-gray-100 px-1 rounded text-xs">+Add</code> zmienił się w <code className="bg-gray-100 px-1 rounded text-xs">Actions</code>.
            </p>
            <p className="text-sm text-gray-700 mt-3">
              Przejdź do <strong>Webhooks</strong>. Wybierz z listy <code className="bg-gray-100 px-1 rounded text-xs">User</code> i ustaw:
            </p>
            <div className="mt-2 space-y-2">
              <div>
                <p className="text-xs font-semibold text-gray-600 mb-1">Callback URL:</p>
                <div className="bg-gray-50 border border-gray-200 rounded p-2 flex items-center gap-2">
                  <code className="text-xs text-gray-900 break-all flex-1 min-w-0">
                    https://xahssxqgkrrlotaunwtg.supabase.co/functions/v1/meta-webhook-dispatcher
                  </code>
                  <CopyBtn text="https://xahssxqgkrrlotaunwtg.supabase.co/functions/v1/meta-webhook-dispatcher" field="guide-webhook-url" />
                </div>
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-600 mb-1">Verify Token:</p>
                <div className="bg-gray-50 border border-gray-200 rounded p-2 flex items-center gap-2">
                  <code className="text-xs text-gray-900 break-all flex-1 min-w-0">
                    social_inbox_verify
                  </code>
                  <CopyBtn text="social_inbox_verify" field="guide-verify-token" />
                </div>
              </div>
            </div>
            <p className="text-sm text-gray-700 mt-3">
              Po weryfikacji zmień status pól w sekcji <em>Webhook fields</em> z <code className="bg-gray-100 px-1 rounded text-xs">Unsubscribed</code> na <code className="bg-gray-100 px-1 rounded text-xs">Subscribed</code>.
            </p>
            <div className="mt-3 bg-red-50 border border-red-200 rounded p-2">
              <p className="text-sm text-red-700 font-semibold">
                ⚠️ Powtórz ten proces dla produktów: Page, Permissions, Application, Instagram oraz Ad Account.
              </p>
            </div>
          </div>

          {/* Step 5 */}
          <div className="bg-white rounded-lg p-4 border-l-4 border-blue-600">
            <h5 className="text-sm font-semibold text-gray-800 mb-2">
              <span className="inline-block bg-blue-600 text-white text-xs px-2 py-0.5 rounded-full mr-2">5</span>
              Publikacja i Login for Business
            </h5>
            <p className="text-sm text-gray-700">
              Kliknij ikonę <strong>Opublikuj</strong> w lewym menu, a następnie niebieski przycisk <strong>Publish</strong>.
            </p>
            <p className="text-sm text-gray-700 mt-2">
              Wejdź w <strong>Facebook Login for Business → Ustawienia</strong> i wklej:
            </p>
            <div className="mt-2 space-y-2">
              <div>
                <p className="text-xs font-semibold text-gray-600 mb-1">Valid OAuth Redirect URIs:</p>
                <div className="bg-gray-50 border border-gray-200 rounded p-2 flex items-center gap-2">
                  <code className="text-xs text-gray-900 break-all flex-1 min-w-0">
                    https://sms.aiagenthub.pl/auth/meta/callback
                  </code>
                  <CopyBtn text="https://sms.aiagenthub.pl/auth/meta/callback" field="guide-oauth-redirect" />
                </div>
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-600 mb-1">Allowed Domains (JS SDK):</p>
                <div className="bg-gray-50 border border-gray-200 rounded p-2 flex items-center gap-2">
                  <code className="text-xs text-gray-900 break-all flex-1 min-w-0">
                    https://sms.aiagenthub.pl/
                  </code>
                  <CopyBtn text="https://sms.aiagenthub.pl/" field="guide-allowed-domain" />
                </div>
              </div>
            </div>
          </div>

          {/* Step 6 */}
          <div className="bg-white rounded-lg p-4 border-l-4 border-green-600">
            <h5 className="text-sm font-semibold text-gray-800 mb-2">
              <span className="inline-block bg-green-600 text-white text-xs px-2 py-0.5 rounded-full mr-2">6</span>
              Połączenie z AI Agent HUB
            </h5>
            <p className="text-sm text-gray-700">
              Otwórz stronę <code className="bg-gray-100 px-1 rounded text-xs">https://sms.aiagenthub.pl</code> i przejdź do <strong>Zaplanowane → API Settings</strong>.
            </p>
            <p className="text-sm text-gray-700 mt-2">
              Skopiuj <strong>APP ID</strong> oraz <strong>App Secret</strong> z ustawień Facebooka i wklej je w odpowiednie pola powyżej. Kliknij <strong>Save API Credentials</strong>.
            </p>
            <p className="text-sm text-gray-700 mt-2">
              Na koniec wejdź w <strong>Social Inbox</strong> i połącz konta Meta oraz Instagram Direct.
            </p>
          </div>

          <p className="text-xs text-gray-400 text-center pt-2">
            © 2026 AI Agent HUB — Instrukcja techniczna integracji
          </p>
        </div>
      )}
    </div>
  );
}
